import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getFieldVisitGroups, getFieldTasks, getFieldActivityStats, deleteFieldVisitGroup, updateFieldTask } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { MasterDataAutocomplete } from '../components/MasterDataAutocomplete';
import { filterFieldVisits, uniqueVisitValues } from '../lib/fieldVisitFilters';
import { toast } from 'sonner';
import {
  MapPin,
  Users,
  Calendar,
  Plus,
  Trash2,
  CheckCircle,
  Clock,
  AlertTriangle,
  ClipboardList,
  TrendingUp,
  Building,
  Pencil,
  ChevronRight,
  BarChart3,
  Filter,
  X
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useScreenState, useScrollRestoration } from '../hooks/useScreenState';

export default function FieldActivityDashboard() {
  const navigate = useNavigate();
  const [visits, setVisits] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [visitToDelete, setVisitToDelete] = useState(null);
  const [visitFilters, setVisitFilters] = useScreenState('fieldActivity.visitFilters', {
    dateFrom: '',
    dateTo: '',
    location: '',
    buyer: '',
  });

  useScrollRestoration('fieldActivity', !loading);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [visitsRes, tasksRes, statsRes] = await Promise.all([
        getFieldVisitGroups(),
        getFieldTasks(),
        getFieldActivityStats()
      ]);
      setVisits(visitsRes.data);
      setTasks(tasksRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load field activity data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVisit = async () => {
    if (!visitToDelete) return;
    try {
      await deleteFieldVisitGroup(visitToDelete);
      toast.success('Grouped visit deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete visit');
    } finally {
      setDeleteDialogOpen(false);
      setVisitToDelete(null);
    }
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    try {
      await updateFieldTask(taskId, { status: newStatus });
      toast.success('Task status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      case 'Pending':
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const isOverdue = (dueDate, status) => {
    if (status === 'Completed') return false;
    const today = new Date().toISOString().split('T')[0];
    return dueDate < today;
  };

  // Get pending/overdue tasks for quick view
  const pendingTasks = tasks.filter(t => t.status !== 'Completed').slice(0, 5);
  const filteredVisits = useMemo(
    () => filterFieldVisits(visits, visitFilters),
    [visitFilters, visits],
  );
  const locationOptions = useMemo(
    () => uniqueVisitValues(visits, 'geography_market').map((location) => ({ value: location, label: location })),
    [visits],
  );
  const buyerOptions = useMemo(
    () => uniqueVisitValues(visits, 'buyer_names').map((buyer) => ({ value: buyer, label: buyer })),
    [visits],
  );
  const hasVisitFilters = Object.values(visitFilters).some(Boolean);

  const clearVisitFilters = () => setVisitFilters({
    dateFrom: '',
    dateTo: '',
    location: '',
    buyer: '',
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-maroon-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="field-activity-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-maroon-900">Field Activity</h1>
          <p className="text-sm text-gray-500 mt-1">Track buyer visits and follow-up tasks</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/field-activity/reports">
            <Button variant="outline" size="sm" data-testid="reports-btn">
              <BarChart3 className="h-4 w-4 mr-1" />
              Reports
            </Button>
          </Link>
          <Link to="/field-activity/tasks">
            <Button variant="outline" size="sm" data-testid="view-all-tasks-btn">
              <ClipboardList className="h-4 w-4 mr-1" />
              All Tasks
            </Button>
          </Link>
          <Link to="/field-activity/new-visit">
            <Button size="sm" className="bg-maroon-600 hover:bg-maroon-700" data-testid="new-visit-btn">
              <Plus className="h-4 w-4 mr-1" />
              New Visit
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="border-l-4 border-l-maroon-500">
            <CardContent className="p-3 sm:p-4 lg:p-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-maroon-600" />
                <div>
                  <p className="text-xs text-gray-500">Total Visits</p>
                  <p className="text-lg lg:text-xl font-bold text-maroon-900">{stats.total_visits}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-3 sm:p-4 lg:p-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-xs text-gray-500">This Month</p>
                  <p className="text-lg lg:text-xl font-bold text-blue-900">{stats.visits_this_month}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="p-3 sm:p-4 lg:p-3">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-yellow-600" />
                <div>
                  <p className="text-xs text-gray-500">Pending Tasks</p>
                  <p className="text-lg lg:text-xl font-bold text-yellow-900">{stats.pending_tasks}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={`border-l-4 ${stats.overdue_tasks > 0 ? 'border-l-red-500' : 'border-l-green-500'}`}>
            <CardContent className="p-3 sm:p-4 lg:p-3">
              <div className="flex items-center gap-2">
                {stats.overdue_tasks > 0 ? (
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                ) : (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                )}
                <div>
                  <p className="text-xs text-gray-500">Overdue</p>
                  <p className={`text-lg lg:text-xl font-bold ${stats.overdue_tasks > 0 ? 'text-red-900' : 'text-green-900'}`}>
                    {stats.overdue_tasks}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Visits List - Takes 2 columns */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base sm:text-lg lg:text-xl flex items-center gap-2">
              <MapPin className="h-5 w-5 text-maroon-600" />
              Recent Visits
            </CardTitle>
          </CardHeader>
          <CardContent>
            {visits.length > 0 && (
              <div className="mb-4 border-b border-gray-100 pb-4" data-testid="recent-visits-filters">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                    <Filter className="h-3.5 w-3.5" />
                    Filter visits
                  </div>
                  {hasVisitFilters && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500" data-testid="recent-visits-filter-count">
                        {filteredVisits.length} of {visits.length}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={clearVisitFilters}
                        className="h-7 px-2 text-xs text-maroon-700"
                        data-testid="clear-visit-filters"
                      >
                        <X className="mr-1 h-3.5 w-3.5" /> Clear
                      </Button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
                  <div>
                    <Label htmlFor="visit-date-from" className="text-xs text-gray-600">From</Label>
                    <Input
                      id="visit-date-from"
                      type="date"
                      value={visitFilters.dateFrom}
                      max={visitFilters.dateTo || undefined}
                      onChange={(event) => setVisitFilters((current) => ({ ...current, dateFrom: event.target.value }))}
                      className="h-9 text-sm"
                      data-testid="visit-date-from-filter"
                    />
                  </div>
                  <div>
                    <Label htmlFor="visit-date-to" className="text-xs text-gray-600">To</Label>
                    <Input
                      id="visit-date-to"
                      type="date"
                      value={visitFilters.dateTo}
                      min={visitFilters.dateFrom || undefined}
                      onChange={(event) => setVisitFilters((current) => ({ ...current, dateTo: event.target.value }))}
                      className="h-9 text-sm"
                      data-testid="visit-date-to-filter"
                    />
                  </div>
                  <div>
                    <Label htmlFor="visit-location-filter" className="text-xs text-gray-600">Location</Label>
                    <MasterDataAutocomplete
                      value={visitFilters.location}
                      onValueChange={(location) => setVisitFilters((current) => ({ ...current, location }))}
                      options={[
                        { value: '', label: 'All Locations', pinned: true },
                        ...locationOptions,
                      ]}
                      placeholder="All Locations"
                      searchPlaceholder="Search locations..."
                      inputClassName="h-9 text-sm"
                      testId="visit-location-filter"
                    />
                  </div>
                  <div>
                    <Label htmlFor="visit-buyer-filter" className="text-xs text-gray-600">Buyer</Label>
                    <MasterDataAutocomplete
                      value={visitFilters.buyer}
                      onValueChange={(buyer) => setVisitFilters((current) => ({ ...current, buyer }))}
                      options={[
                        { value: '', label: 'All Buyers', pinned: true },
                        ...buyerOptions,
                      ]}
                      placeholder="All Buyers"
                      searchPlaceholder="Search buyers..."
                      inputClassName="h-9 text-sm"
                      testId="visit-buyer-filter"
                    />
                  </div>
                </div>
              </div>
            )}
            {visits.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <MapPin className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No visits recorded yet</p>
                <Link to="/field-activity/new-visit">
                  <Button size="sm" className="mt-3 bg-maroon-600 hover:bg-maroon-700">
                    <Plus className="h-4 w-4 mr-1" />
                    Record First Visit
                  </Button>
                </Link>
              </div>
            ) : filteredVisits.length === 0 ? (
              <div className="text-center py-8 text-gray-500" data-testid="recent-visits-no-results">
                <MapPin className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No visits match these filters</p>
                <Button type="button" variant="link" size="sm" onClick={clearVisitFilters} className="text-maroon-700" data-testid="clear-empty-visit-filters">
                  Clear filters
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredVisits.slice(0, 10).map((visit) => (
                  <div
                    key={visit.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/field-activity/group/${visit.id}`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigate(`/field-activity/group/${visit.id}`);
                      }
                    }}
                    className="flex items-start justify-between gap-3 p-3 bg-white border border-gray-200 border-l-4 border-l-maroon-500 rounded-md hover:border-maroon-300 hover:bg-maroon-50/40 transition-[background-color,border-color] cursor-pointer focus:outline-none focus:ring-2 focus:ring-maroon-500"
                    data-testid={`visit-group-row-${visit.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <Badge className="bg-maroon-100 text-maroon-900 font-mono text-xs" data-testid={`visit-code-${visit.id}`}>
                          {visit.visit_code}
                        </Badge>
                        <span className="flex items-center gap-1 text-xs text-gray-600">
                          <MapPin className="h-3 w-3" />
                          {visit.geography_market}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-gray-600">
                          <Calendar className="h-3 w-3" />
                          {formatDate(visit.visit_date)}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5" data-testid={`visit-buyers-${visit.id}`}>
                        <Building className="h-3.5 w-3.5 text-maroon-600" />
                        {visit.buyer_names.map((buyerName) => (
                          <span key={buyerName} className="px-2 py-0.5 bg-gray-100 rounded text-xs font-medium text-gray-700">
                            {buyerName}
                          </span>
                        ))}
                      </div>
                      {visit.visit_summary && (
                        <p className="mt-2 text-xs text-gray-500 line-clamp-2" data-testid={`visit-summary-${visit.id}`}>
                          {visit.visit_summary}
                        </p>
                      )}
                      {visit.staff_names?.length > 0 && (
                        <span className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                          <Users className="h-3 w-3" />
                          {visit.staff_names.join(', ')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/field-activity/group/${visit.id}?edit=summary`);
                        }}
                        aria-label={`Edit visit ${visit.visit_code}`}
                        data-testid={`edit-visit-group-${visit.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(event) => {
                          event.stopPropagation();
                          setVisitToDelete(visit.id);
                          setDeleteDialogOpen(true);
                        }}
                        aria-label={`Delete visit ${visit.visit_code}`}
                        data-testid={`delete-visit-group-${visit.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {filteredVisits.length > 10 && (
                  <div className="text-center pt-2">
                    <span className="text-xs text-gray-500" data-testid="recent-visits-result-limit">
                      Showing 10 of {filteredVisits.length} visits
                    </span>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tasks Panel */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base sm:text-lg lg:text-xl flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-maroon-600" />
                Tasks
              </CardTitle>
              <Link to="/field-activity/tasks">
                <Button variant="ghost" size="sm" className="text-maroon-600">
                  View All
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {pendingTasks.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <CheckCircle className="h-10 w-10 mx-auto mb-2 text-green-300" />
                <p className="text-sm">All caught up!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {pendingTasks.map((task) => (
                  <div
                    key={task.id}
                    className={`p-3 rounded-lg border ${
                      isOverdue(task.due_date, task.status)
                        ? 'border-red-200 bg-red-50'
                        : 'border-gray-200 bg-white'
                    }`}
                    data-testid={`task-row-${task.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{task.description}</p>
                        <p className="text-xs text-gray-500 mt-1">{task.buyer_name}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className={`text-xs ${getStatusColor(task.status)}`}>
                            {task.status}
                          </Badge>
                          <span className={`text-xs ${isOverdue(task.due_date, task.status) ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                            Due: {formatDate(task.due_date)}
                          </span>
                        </div>
                      </div>
                      {task.status !== 'Completed' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => handleTaskStatusChange(task.id, 'Completed')}
                          title="Mark as completed"
                          data-testid={`complete-task-${task.id}`}
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Visit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this grouped visit? This will delete every buyer detail and associated task in the visit.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteVisit}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
